import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Check, Trash2, UserPlus } from 'lucide-react';
import moment from 'moment';

import {
  useInviteNonFFUserMutation,
  useOrgMemberLifecycleMutation,
  useOrgMemberRoleMutation,
  useOrgMembersQuery,
  useOrgRoleCatalogQuery,
  type OrgMemberAction,
} from '@/client/api/admin/org-members';
import type { AdminOrgMember } from '@/client/types/admin/users-orgs';
import Pagination from '@/client/components/common/Pagination';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';

const MEMBERS_PAGE_SIZE = 20;

const MEMBER_ACTION_COPY: Record<
  OrgMemberAction,
  { label: string; title: string; body: string; destructive: boolean }
> = {
  activate: {
    label: 'Activate',
    title: 'Activate member?',
    body: 'The member regains access to the organization.',
    destructive: false,
  },
  deactivate: {
    label: 'Deactivate',
    title: 'Deactivate member?',
    body: 'The member loses access but stays on the roster.',
    destructive: true,
  },
  delete: {
    label: 'Remove',
    title: 'Remove member?',
    body: 'This is a soft delete. It ALSO drops every organization role assigned to this member — undelete will not bring those roles back.',
    destructive: true,
  },
  undelete: {
    label: 'Restore',
    title: 'Restore member?',
    body: 'The membership row is restored, but the organization roles dropped at delete time are NOT restored. You must re-assign them afterwards.',
    destructive: false,
  },
  onboard: {
    label: 'Mark onboarded',
    title: 'Mark member as onboarded?',
    body: 'Flags the member as having completed onboarding for this organization.',
    destructive: false,
  },
};

const cleanMemberField = (v?: string | null): string => {
  const s = (v ?? '').trim();
  return s && s.toLowerCase() !== 'unknown' ? s : '';
};

const memberDisplayName = (m: AdminOrgMember): string => {
  const name = [cleanMemberField(m.user?.first_name), cleanMemberField(m.user?.last_name)]
    .filter(Boolean)
    .join(' ');
  return name || cleanMemberField(m.user?.username) || `uid ${m.uid}`;
};

const MembersTab = ({ oid }: { oid: number }) => {
  const membersQuery = useOrgMembersQuery(oid);
  const roleCatalog = useOrgRoleCatalogQuery({ permissions: 0 });

  const lifecycleMutation = useOrgMemberLifecycleMutation();
  const inviteMutation = useInviteNonFFUserMutation();
  const roleMutation = useOrgMemberRoleMutation();

  const members = useMemo(() => membersQuery.data?.members ?? [], [membersQuery.data]);

  const [page, setPage] = useState(1);
  const pageMembers = members.slice((page - 1) * MEMBERS_PAGE_SIZE, page * MEMBERS_PAGE_SIZE);

  const [pending, setPending] = useState<{
    member: AdminOrgMember;
    action: OrgMemberAction;
  } | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addEmails, setAddEmails] = useState('');
  const [addRole, setAddRole] = useState('');

  const [roleTarget, setRoleTarget] = useState<AdminOrgMember | null>(null);

  const runLifecycle = async () => {
    if (!pending) return;
    const copy = MEMBER_ACTION_COPY[pending.action];
    const toastId = toast.loading(`${copy.label}…`);
    try {
      await lifecycleMutation.mutateAsync({
        oid,
        uid: pending.member.uid,
        action: pending.action,
      });
      toast.success(`${copy.label} succeeded`, {
        id: toastId,
        description:
          pending.action === 'undelete'
            ? 'Membership restored. Organization roles were NOT restored — re-assign them now.'
            : `Member ${pending.member.uid} was updated.`,
        richColors: true,
        duration: pending.action === 'undelete' ? 8000 : 4000,
      });
    } catch (error: any) {
      toast.error(`${copy.label} failed`, {
        id: toastId,
        description: getApiErrorMessage(error, 'Something went wrong.'),
        richColors: true,
        duration: 5000,
      });
    } finally {
      setPending(null);
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const emails = addEmails
      .split(',')
      .map(value => value.trim())
      .filter(Boolean);
    if (emails.length === 0) return;
    if (!addRole) {
      toast.error('Pick a role for the invite.');
      return;
    }

    const toastId = toast.loading(`Inviting ${emails.length} member(s)…`);
    try {
      await inviteMutation.mutateAsync({
        oid,
        role: addRole,
        email: emails.join(','),
      });
      toast.success(
        `Invite sent to ${emails.length} email${emails.length === 1 ? '' : 's'}`,
        { id: toastId, richColors: true }
      );
      setAddOpen(false);
      setAddEmails('');
      setAddRole('');
    } catch (error: any) {
      toast.error('Failed to send invite', {
        id: toastId,
        description: getApiErrorMessage(error, 'Something went wrong.'),
        richColors: true,
        duration: 5000,
      });
    }
  };

  const toggleRole = async (uid: number, ourid: number, assigned: boolean, roleName: string) => {
    const toastId = toast.loading(assigned ? `Assigning ${roleName}…` : `Removing ${roleName}…`);
    try {
      await roleMutation.mutateAsync({ oid, uid, ourid, assigned });
      toast.success(assigned ? `Assigned ${roleName}` : `Removed ${roleName}`, {
        id: toastId,
        richColors: true,
      });
    } catch (error: any) {
      toast.error(assigned ? `Failed to assign ${roleName}` : `Failed to remove ${roleName}`, {
        id: toastId,
        description: getApiErrorMessage(error, 'Something went wrong.'),
        richColors: true,
        duration: 5000,
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Members</h2>
          <p className="text-sm text-gray-500">
            {membersQuery.data?.count ?? members.length} on the roster, including the owner.
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)}>
          <UserPlus className="mr-2 h-4 w-4" />
          Invite member
        </Button>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['User', 'Role', 'State', 'Onboarded', 'Joined', ''].map((heading, index) => (
                <th
                  key={heading || index}
                  className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${
                    heading === '' ? 'text-right' : 'text-left'
                  }`}
                >
                  {heading}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {membersQuery.isLoading &&
              Array.from({ length: 5 }).map((_, index) => (
                <tr key={index}>
                  {Array.from({ length: 6 }).map((__, cell) => (
                    <td key={cell} className="px-6 py-4">
                      <Skeleton className="h-4 w-20" />
                    </td>
                  ))}
                </tr>
              ))}

            {!membersQuery.isLoading && members.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                  This organization has no members.
                </td>
              </tr>
            )}

            {pageMembers.map(member => {
              const email = cleanMemberField(member.user?.email);
              return (
                <tr key={member.ouid ?? member.uid}>
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">
                        {memberDisplayName(member)}
                      </span>
                      {member.is_owner && (
                        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700">
                          Owner
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">{email || `uid ${member.uid}`}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {member.role?.role_friendly || member.role_description || '—'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {member.deleted === 1 ? (
                        <Badge variant="destructive">Removed</Badge>
                      ) : member.active === 1 ? (
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {member.onboarded === 1 ? 'Yes' : 'No'}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {member.date_added ? moment(member.date_added).format('YYYY-MM-DD') : '—'}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => setRoleTarget(member)}>
                        Organization roles
                      </Button>
                      {member.deleted === 1 ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPending({ member, action: 'undelete' })}
                        >
                          Restore
                        </Button>
                      ) : (
                        <>
                          {member.active === 1 ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setPending({ member, action: 'deactivate' })}
                            >
                              Deactivate
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setPending({ member, action: 'activate' })}
                            >
                              Activate
                            </Button>
                          )}
                          {member.onboarded !== 1 && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setPending({ member, action: 'onboard' })}
                            >
                              Onboard
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600"
                            onClick={() => setPending({ member, action: 'delete' })}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {members.length > MEMBERS_PAGE_SIZE && (
          <div className="border-t border-gray-200 px-4 py-3">
            <Pagination
              total={members.length}
              page={page}
              limit={MEMBERS_PAGE_SIZE}
              setPage={setPage}
              showButtons
            />
          </div>
        )}
      </div>

      <AlertDialog
        open={Boolean(pending)}
        onOpenChange={next => {
          if (!next) setPending(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pending ? MEMBER_ACTION_COPY[pending.action].title : ''}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pending ? MEMBER_ACTION_COPY[pending.action].body : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={
                pending && MEMBER_ACTION_COPY[pending.action].destructive
                  ? 'bg-red-600 hover:bg-red-700'
                  : undefined
              }
              disabled={lifecycleMutation.isPending}
              onClick={e => {
                e.preventDefault();
                void runLifecycle();
              }}
            >
              {lifecycleMutation.isPending
                ? 'Working…'
                : pending
                  ? MEMBER_ACTION_COPY[pending.action].label
                  : 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={addOpen}
        onOpenChange={next => {
          if (!next) setAddOpen(false);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invite members by email</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="add-emails">
                Email address(es) <span className="text-red-500">*</span>
              </Label>
              <Input
                id="add-emails"
                type="text"
                placeholder="e.g. ama@example.com, kojo@example.com"
                value={addEmails}
                onChange={e => setAddEmails(e.target.value)}
                required
              />
              <p className="text-xs text-gray-500">
                Comma-separated. Each person gets an email invite and joins the organization
                once they accept.
              </p>
            </div>

            <div className="space-y-1">
              <Label htmlFor="add-role">
                Organization role <span className="text-red-500">*</span>
              </Label>
              <select
                id="add-role"
                className="h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
                value={addRole}
                onChange={e => setAddRole(e.target.value)}
              >
                <option value="">Select a role</option>
                {(roleCatalog.data ?? []).map(role => (
                  <option key={role.ourid} value={role.role}>
                    {role.role_friendly || role.role}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500">
                One role applies to everyone in this invite. Organization roles are separate
                from account roles.
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={inviteMutation.isPending || !addEmails.trim() || !addRole}
              >
                {inviteMutation.isPending ? 'Sending…' : 'Send invite'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(roleTarget)}
        onOpenChange={next => {
          if (!next) setRoleTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Organization roles — uid {roleTarget?.uid}</DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
              These are <span className="font-semibold">organization roles</span> — they govern
              what this user can do inside this organization only. They are a different thing
              from <span className="font-semibold">account roles</span>, which are platform-wide
              and are managed on the Roles page.
            </div>

            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              The roster endpoint does not report which organization roles a member currently
              holds, so this list cannot show the current state. Assign and remove are explicit
              actions; each one reports its own result.
            </div>

            <div className="divide-y rounded-md border border-gray-200">
              {(roleCatalog.data ?? []).map(role => (
                <div key={role.ourid} className="flex items-center justify-between gap-3 p-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {role.role_friendly || role.role}
                    </p>
                    <p className="text-xs text-gray-500">
                      {role.description || `ourid ${role.ourid}`}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={roleMutation.isPending}
                      onClick={() =>
                        roleTarget &&
                        toggleRole(
                          roleTarget.uid,
                          role.ourid,
                          true,
                          role.role_friendly || role.role
                        )
                      }
                    >
                      <Check className="mr-1 h-3 w-3" />
                      Assign
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600"
                      disabled={roleMutation.isPending}
                      onClick={() =>
                        roleTarget &&
                        toggleRole(
                          roleTarget.uid,
                          role.ourid,
                          false,
                          role.role_friendly || role.role
                        )
                      }
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}

              {(roleCatalog.data ?? []).length === 0 && (
                <p className="p-4 text-sm text-gray-500">No organization roles are defined.</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleTarget(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MembersTab;
